<template>
  <div>
    <Head title="Transportistas" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Transportistas</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Create Button -->
        <Link 
          href="/carriers/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          Nuevo Transportista
        </Link>
      </div>
    </div>

    <!-- Search Tool Bar -->
    <div class="flex gap-4 mb-6">
      <div class="relative flex-1">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <icon name="search" class="w-4 h-4 text-gray-400" />
        </div>
        <input 
          v-model="form.search.terms" 
          type="text" 
          class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors shadow-sm" 
          placeholder="Buscar transportista por nombre o documento..."
        />
      </div>
    </div>

    <!-- Content Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div v-for="carrier in carriers.data" :key="carrier.id" class="p-4 hover:bg-gray-50 transition-colors">
          <Link :href="`carriers/${carrier.id}/edit`" class="block space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center overflow-hidden flex-shrink-0 border border-orange-100">
                <i class="fa fa-truck text-orange-400 text-xl" />
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <h3 class="font-bold text-gray-900 line-clamp-1">{{ carrier.name }}</h3>
                  <span 
                    class="ml-2 px-2 py-1 rounded-full text-xs font-bold"
                    :class="carrier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  >
                    {{ carrier.is_active ? 'Activo' : 'Inactivo' }}
                  </span>
                </div>
                <p class="text-sm text-gray-500 font-medium mt-1">{{ carrier.document_number || 'Sin documento' }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 text-sm text-gray-600 border-t border-gray-100 pt-2">
              <div>
                <span class="block text-xs text-gray-400">Teléfono</span>
                <span class="font-medium text-gray-800">{{ carrier.contact_phone || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs text-gray-400">Email</span>
                <span class="font-medium text-gray-800">{{ carrier.contact_email || 'N/A' }}</span>
              </div>
            </div>
          </Link>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/50 text-left border-b border-gray-100">
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider max-w-[200px]">Nombre</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">RUT/Doc</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Teléfono</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Email</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-center">Estado</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="carrier in carriers.data" 
              :key="carrier.id"
              class="group hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4 max-w-[200px] whitespace-normal">
                <Link :href="`carriers/${carrier.id}/edit`" class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center border border-orange-100">
                    <i class="fa fa-truck text-orange-400 text-sm" />
                  </div>
                  <span class="text-gray-900 font-medium hover:text-orange-600 line-clamp-1">{{ carrier.name }}</span>
                </Link>
              </td>
              
              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ carrier.document_number || 'N/A' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ carrier.contact_phone || 'N/A' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ carrier.contact_email || 'N/A' }}
              </td>

              <td class="px-6 py-4 text-center">
                <span 
                  class="inline-block px-2 py-1 rounded-full text-xs font-bold"
                  :class="carrier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                >
                  {{ carrier.is_active ? 'Activo' : 'Inactivo' }}
                </span>
              </td>

              <td class="px-6 py-4 text-right">
                <Link 
                  :href="`carriers/${carrier.id}/edit`" 
                  class="text-gray-400 hover:text-orange-500 transition-colors mx-2"
                >
                  <i class="fa fa-edit" />
                </Link>
              </td>
            </tr>

            <tr v-if="!carriers?.data?.length">
              <td colspan="6" class="py-20 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-truck text-2xl opacity-50" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-1">No hay transportistas registrados</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Empieza por crear un nuevo transportista para asignarlo a tus despachos.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-6 flex justify-center">
      <pagination 
        v-if="carriers?.meta?.links"
        :links="carriers.meta.links"
        :query-params="currentQueryParams"
      />
    </div>

    <!-- Quick Links (Footer) -->
    <div class="mt-8 pt-6 border-t border-gray-100">
      <h3 class="text-sm font-medium text-gray-700 mb-3 ml-1">Logística</h3>
      <div class="flex flex-wrap gap-3">
        <Link 
          href="/shipments"
          class="px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2 transition-colors text-sm font-medium hover:border-orange-200"
        >
          <i class="fa fa-boxes text-orange-400" />
          Guías de Despacho
        </Link>
        <Link
          href="/transport-units" 
          class="px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2 transition-colors text-sm font-medium hover:border-orange-200"
        >
          <i class="fa fa-car text-orange-400" />
          Unidades de Transporte
        </Link>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'CarriersIndex',
  
  components: {
    Pagination,
    Head,
    Icon,
    Link,
  },

  layout: Layout,

  props: {
    carriers: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: this.$inertia.form({
        search: {
          terms: '',
        },
      }),
    }
  },

  computed: {
    currentQueryParams() {
      return { search: pickBy(this.form.search) }
    },
  },

  watch: {
    'form.search': {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/carriers',
          { search: pickBy(this.form.search), page: 1 },
          { preserveState: true },
        )
      }, 300),
    },
  },
}
</script>
